import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';

import * as mapboxgl from 'mapbox-gl';

import { CoordinatePair, Polygon, PositionalData } from '@canvas/canvas-lib';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

type Position = [number, number];

@Component({
  selector: 'canvas-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
  standalone: false,
})
export class MapComponent implements OnInit, OnDestroy {
  @ViewChild('mapContainer', { static: true }) mapContainer!: ElementRef;

  private firstDraw: boolean = true;
  private loadDataSubject = new Subject<void>();
  private positionalDataSubject = new BehaviorSubject<PositionalData>({
    coordinates: [],
    polygons: [],
  });

  map!: mapboxgl.Map;

  private markers: mapboxgl.Marker[] = [];
  private sourceIds: string[] = [];

  @Input() accessToken: string = '';

  @Input() set positionalData(value: PositionalData) {
    this.positionalDataSubject.next(value);
  }

  @Input() initialStyle = 'satellite-streets-v12';
  @Input() zoom = 8;
  @Output() mapClicked = new EventEmitter<CoordinatePair>();

  constructor() {
    combineLatest({
      positionalData: this.positionalDataSubject,
      loadData: this.loadDataSubject,
    })
      .pipe(takeUntilDestroyed())
      .subscribe(({ positionalData }) => {
        this.drawMarkersAndPolygons(positionalData);
      });
  }

  currentStyle = 'satellite-streets-v12';

  private style = 'mapbox://styles/mapbox/satellite-streets-v12';

  ngOnInit() {
    mapboxgl as typeof mapboxgl;

    this.currentStyle = this.initialStyle;
    this.style = `mapbox://styles/mapbox/${this.initialStyle}`;

    this.map = new mapboxgl.Map({
      container: this.mapContainer.nativeElement,
      accessToken: this.accessToken,
      style: this.style,
      zoom: this.zoom,
    });

    this.map.on('mouseup', (e: any) => {
      const lngLat = e.lngLat.wrap();
      const coords = {
        longitude: lngLat.lng,
        latitude: lngLat.lat,
      } as CoordinatePair;

      this.mapClicked.emit(coords);
    });

    this.map.addControl(new mapboxgl.NavigationControl());

    this.map.on('style.load', () => {
      this.loadDataSubject.next();
    });
  }

  ngOnDestroy(): void {
    if (this.map) {
      this.map.remove();
      this.map = null as any;
    }
  }

  private drawMarkersAndPolygons(positionalData: PositionalData) {
    const markerPositions = positionalData?.coordinates
      ? this.coordinatesToPositions(positionalData?.coordinates)
      : [];

    this.drawMarkers(markerPositions);
    this.drawPolygons(positionalData?.polygons || []);

    if (this.hasPolygonCoordinates(positionalData)) {
      const { bounds, options } = this.getBoundsAndOptions(positionalData);
      this.map.fitBounds(bounds, options);
    } else if (this.hasPinCoordinates(positionalData)) {
      this.map.setCenter(this.getCentre(positionalData));
      this.map.zoomTo(this.zoom);
    } else {
      // Kenya
      this.map.fitBounds(
        [
          [32.958984, -5.353521],
          [43.50585, 5.615985],
        ],
        { duration: 0 }
      );
    }

    this.firstDraw = false;
  }

  private drawMarkers(positions: Position[]) {
    this.removeAllMarkersFromMap();
    this.addMarkersToMap(positions);
  }

  private drawPolygons(polygons: Polygon[]) {
    this.removeAllPolygonsFromMap();

    for (let i = 0; i < polygons.length; i++) {
      this.drawPolygon(`polygon-${i + 1}`, this.map as any, polygons[i]);
    }
  }

  private removeAllPolygonsFromMap() {
    for (const sourceId of this.sourceIds) {
      if (this.map.getLayer(`${sourceId}-fill`)) {
        this.map.removeLayer(`${sourceId}-fill`);
      }

      if (this.map.getLayer(`${sourceId}-outline`)) {
        this.map.removeLayer(`${sourceId}-outline`);
      }

      if (this.map.getSource(sourceId)) {
        this.map.removeSource(sourceId);
      }
    }

    this.sourceIds = [];
  }

  private coordinatesToPositions(
    coordinates: Array<CoordinatePair>
  ): Position[] {
    return coordinates && coordinates.length > 0
      ? coordinates.map((x) => [x.longitude, x.latitude])
      : [];
  }

  private drawPolygon(label: string, map: mapboxgl.Map, polygon: Polygon) {
    const positions = this.coordinatesToPositions(polygon.coordinates);

    const coordinates = positions.map((p) => {
      return [p[0], p[1]];
    });

    map.addSource(label, {
      type: 'geojson',
      data: {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'Polygon',
          coordinates: [coordinates],
        },
      },
    });

    this.fillPolygon({ map, source: label });
    this.addOutlineToPolygon({ map, source: label });
    this.sourceIds.push(label);
  }

  private fillPolygon({ map, source }: { map: mapboxgl.Map; source: string }) {
    map.addLayer({
      id: `${source}-fill`,
      type: 'fill',
      source, // reference the data source
      layout: {},
      paint: {
        'fill-color': '#0080ff',
        'fill-opacity': 0.3,
      },
    });
  }

  private addOutlineToPolygon({
    map,
    source,
  }: {
    map: mapboxgl.Map;
    source: string;
  }) {
    map.addLayer({
      id: `${source}-outline`,
      type: 'line',
      source: source,
      layout: {},
      paint: {
        'line-color': '#000',
        'line-width': 1,
      },
    });
  }

  private addMarkersToMap(positions: Position[]) {
    for (const position of positions) {
      const markerElement = document.createElement('div');
      markerElement.className = 'canvas-map-pin';
      markerElement.style.width = '18px';
      markerElement.style.height = '18px';
      markerElement.style.borderRadius = '999px';
      markerElement.style.border = '2px solid #ffffff';
      markerElement.style.background = '#d52f1b';
      markerElement.style.boxShadow = '0 0 0 4px rgba(213, 47, 27, 0.25)';
      markerElement.style.cursor = 'pointer';

      const marker = new mapboxgl.Marker({ element: markerElement, anchor: 'bottom' })
        .setLngLat(position)
        .addTo(this.map)
        .setPopup(new mapboxgl.Popup().setHTML('<h4>Device 1</h4>'));

      this.markers.push(marker);
    }
  }

  private removeAllMarkersFromMap() {
    for (const marker of this.markers) {
      marker.remove();
    }
    this.markers = [];
  }

  private getBoundsAndOptions(positionalData: PositionalData): {
    bounds: mapboxgl.LngLatBoundsLike;
    options: mapboxgl.EasingOptions;
  } {
    let minLongitude: number;
    let maxLongitude: number;
    let minLatitude: number;
    let maxLatitude: number;

    const coordinates = this.getAllCoordinates(positionalData);

    minLatitude = maxLatitude = coordinates[0].latitude;
    minLongitude = maxLongitude = coordinates[0].longitude;

    for (let index = 1; index < coordinates.length; index++) {
      const coordinate = coordinates[index];

      minLatitude = Math.min(minLatitude, coordinate.latitude);
      maxLatitude = Math.max(maxLatitude, coordinate.latitude);
      minLongitude = Math.min(minLongitude, coordinate.longitude);
      maxLongitude = Math.max(maxLongitude, coordinate.longitude);
    }

    return {
      bounds: [
        minLongitude /* west */,
        minLatitude /* south */,
        maxLongitude /* east */,
        maxLatitude /* north */,
      ],
      options: { padding: 150, duration: this.firstDraw ? 0 : 1000 },
    };
  }

  private getAllCoordinates(positionalData: PositionalData): CoordinatePair[] {
    const coordinates: CoordinatePair[] = [];

    if (positionalData?.polygons?.length > 0) {
      for (const polygon of positionalData.polygons) {
        for (const coordinate of polygon.coordinates) {
          coordinates.push(coordinate);
        }
      }
    }

    if (positionalData?.coordinates?.length > 0) {
      for (const coordinate of positionalData.coordinates) {
        coordinates.push(coordinate);
      }
    }

    return coordinates;
  }

  private hasPolygonCoordinates(positionalData: PositionalData) {
    if (positionalData?.polygons?.length > 0) {
      for (const polygon of positionalData.polygons) {
        if (polygon.coordinates.length > 0) {
          return true;
        }
      }
    }

    return false;
  }

  private hasPinCoordinates(positionalData: PositionalData) {
    return positionalData?.coordinates?.length > 0;
  }

  private getCentre(positionalData: PositionalData): mapboxgl.LngLatLike {
    let longitudeTotal = 0;
    let latitudeTotal = 0;
    let count = 0;

    var coordinates = this.getAllCoordinates(positionalData);

    for (const coordinate of coordinates) {
      longitudeTotal += coordinate.longitude;
      latitudeTotal += coordinate.latitude;
      count++;
    }

    const averageLongitude = longitudeTotal / count;
    const averageLatitude = latitudeTotal / count;

    return [averageLongitude, averageLatitude];
  }

  handleChangeStyle(style: string) {
    this.currentStyle = style;
    this.style = `mapbox://styles/mapbox/${style}`;
    this.map?.setStyle(this.style);
  }

  isChecked(val: string) {
    const isChecked = val === this.currentStyle;
    return isChecked;
  }
}
